import React, { useEffect, useRef } from "react";
import { useCart } from "./cartStore";

/**
 * Cart screen with PayPal checkout.
 */
export default function Cart({ t, lang, setScreen, track }) {
  const { items, removeItem, updateQty, clear, totalCount, subtotal, total, discount } = useCart();
  const paypalRef = useRef(null);

  useEffect(() => {
    if (!paypalRef.current) return;
    paypalRef.current.innerHTML = "";

    if (items.length === 0) return;

    if (!window.paypal || typeof window.paypal.Buttons !== "function") {
      paypalRef.current.innerHTML = `
        <div style="padding:14px;border-radius:12px;border:1px solid rgba(212,175,55,.25);background:rgba(212,175,55,.06);color:#D4AF37;font-weight:800;text-align:center;">
          Paiement sécurisé disponible au moment de la commande.
        </div>`;
      return;
    }

    const description = items
      .map((i) => `${i.qty}x ${i.modelLabel || i.model} ${i.colorLabel || i.color} — ${i.petName || "Pet"}`)
      .join(" | ")
      .slice(0, 127);

    let buttons;
    try {
      buttons = window.paypal.Buttons({
        style: { layout: "vertical", color: "gold", shape: "rect", label: "paypal" },
        createOrder: (_d, actions) =>
          actions.order.create({
            purchase_units: [
              {
                description,
                amount: { value: total.toFixed(2), currency_code: "CAD" },
              },
            ],
          }),
        onApprove: async (_d, actions) => {
          try {
            await actions.order.capture();
            track && track("purchase", {
              value: Number(total.toFixed(2)),
              currency: "CAD",
              quantity: totalCount,
              items: items.map((i) => ({ model: i.model, color: i.color })),
            });
            clear();
            alert(t.cart_thanks || "Merci! Votre commande a été reçue.");
            setScreen && setScreen("home");
          } catch (e) {
            console.error(e);
            alert("Paiement non confirmé. Réessayez.");
          }
        },
        onError: (e) => {
          console.error("PayPal error:", e);
        },
      });
      if (buttons && typeof buttons.render === "function") {
        buttons.render(paypalRef.current).catch((e) => console.error(e));
      }
    } catch (e) {
      console.error(e);
    }

    return () => {
      if (paypalRef.current) paypalRef.current.innerHTML = "";
    };
  }, [items, total, totalCount, clear, t, setScreen, track]);

  if (items.length === 0) {
    return (
      <section className="page-card cart-empty">
        <h2>{t.cart_title || "Panier"}</h2>
        <p>{t.cart_empty || "Votre panier est vide."}</p>
        <button className="gold-btn" onClick={() => setScreen("create")} data-testid="cart-empty-cta">
          {t.cart_empty_cta || t.hero_cta || "Créer ma médaille"}
        </button>
      </section>
    );
  }

  return (
    <section className="page-card cart-page" data-testid="cart-page">
      <div className="cart-head">
        <h2>{t.cart_title || "Panier"}</h2>
        <p>{t.cart_subtitle || `${totalCount} article${totalCount > 1 ? "s" : ""} prêt${totalCount > 1 ? "s" : ""} à expédier`}</p>
      </div>

      <div className="cart-grid">
        <ul className="cart-items" data-testid="cart-items">
          {items.map((i) => (
            <li key={i.id} className="cart-item" data-testid={`cart-item-${i.id}`}>
              <div className="cart-thumb" style={{ background: i.colorHex || "#222" }}>
                <span className="cart-thumb-name" style={{ fontFamily: i.font || "Montserrat" }}>
                  {(i.petName || "ANIMAUX").toUpperCase()}
                </span>
              </div>
              <div className="cart-meta">
                <strong>{i.modelLabel || i.model}</strong>
                <span>{i.colorLabel || i.color} · {i.petName || "—"}</span>
                {i.icon && i.icon !== "none" && <span className="cart-meta-tag">+ {i.iconLabel || i.icon}</span>}
                {i.phone && <span className="cart-meta-line">☎ {i.phone}</span>}
                {i.addr && <span className="cart-meta-line">📍 {i.addr}</span>}
                {i.extra && <span className="cart-meta-line">{i.extra}</span>}
              </div>
              <div className="cart-actions">
                <div className="cart-qty">
                  <button onClick={() => updateQty(i.id, i.qty - 1)} aria-label="Diminuer">−</button>
                  <span>{i.qty}</span>
                  <button onClick={() => updateQty(i.id, i.qty + 1)} aria-label="Augmenter">+</button>
                </div>
                <div className="cart-price">${(i.unitPrice * i.qty).toFixed(2)}</div>
                <button className="cart-remove" onClick={() => removeItem(i.id)} aria-label="Retirer">
                  {t.cart_remove || "Retirer"}
                </button>
              </div>
            </li>
          ))}
        </ul>

        <aside className="cart-summary" data-testid="cart-summary">
          <h3>{t.cart_summary || "Résumé"}</h3>
          <div className="cart-summary-row">
            <span>{t.cart_subtotal || "Sous-total"}</span>
            <span>${subtotal.toFixed(2)}</span>
          </div>
          {discount > 0 && (
            <div className="cart-summary-row discount">
              <span>{t.cart_discount || "Remise"} ({Math.round(discount * 100)}%)</span>
              <span>−${(subtotal * discount).toFixed(2)}</span>
            </div>
          )}
          <div className="cart-summary-row">
            <span>{t.cart_shipping || "Livraison"}</span>
            <span className="cart-free">{t.cart_free || "Gratuite"}</span>
          </div>
          <div className="cart-summary-row total">
            <span>{t.total_label || "Total"}</span>
            <span>${total.toFixed(2)} CAD</span>
          </div>

          <div className="cart-trust">
            🔒 {t.checkout_note || "Paiement sécurisé via PayPal"}
          </div>

          <div ref={paypalRef} data-testid="cart-paypal" className="cart-paypal" />

          <button className="cart-clear" onClick={clear} type="button">
            {t.cart_clear || "Vider le panier"}
          </button>
        </aside>
      </div>
    </section>
  );
}
